import { Email } from "@mui/icons-material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Popover,
  useTheme,
} from "@mui/material";
import { blue } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useState } from "react";

import { isPlatformAdminProfile } from "../helpers/get-user-role";
import { getUserInitials, stringToColor } from "../helpers/user-utils";
import { Profile } from "../types/profile";
import { ChangeLanguageButton } from "./change-language-button";

export type AccountSettingsDropdownState = {
  /** User profile */
  userProfile?: Profile;
  selectedLanguage: SupportedLanguage;
};

export type AccountSettingsDropdownEvent =
  | "logout"
  | "open-admin"
  | "change-language";

export type AccountSettingsDropdownEventPayload = {
  language?: SupportedLanguage;
};

export const AccountSettingsDropdown: PimoReactComponent<
  AccountSettingsDropdownState,
  AccountSettingsDropdownEvent,
  AccountSettingsDropdownEventPayload
> = ({ userProfile, selectedLanguage: language, fireEvent }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    fireEvent?.("logout", {});
  };

  const handleOpenAdmin = () => {
    handleClose();
    fireEvent?.("open-admin", {});
  };

  const isPlatformAdmin = isPlatformAdminProfile(userProfile);
  const userInitials = getUserInitials(userProfile);
  const fullName = userProfile?.firstName || userProfile?.name || "";
  const avatarColor = stringToColor(fullName);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleClick}
        aria-label="account settings"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          border: "none",
          background: "transparent",
          borderColor: blue[50],
          borderRadius: theme.shape.borderRadius,
          padding: "7px 10px",
          minWidth: 0,
          outline: "none",
          "&:hover": {
            backgroundColor: blue[50],
          },
        }}
      >
        <Avatar
          sx={{
            backgroundColor: avatarColor,
            width: 35,
            height: 35,
            pointerEvents: "none",
          }}
        >
          {userInitials}
        </Avatar>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{ "@media print": { display: "none" } }}
      >
        <Box sx={{ maxWidth: 450 }}>
          <List>
            {userProfile?.email && (
              <>
                <ListSubheader sx={{ color: theme.palette.text.primary }}>
                  {userProfile.email}
                </ListSubheader>
                <Divider />
              </>
            )}
            {isPlatformAdmin && (
              <ListItem>
                <ListItemButton onClick={handleOpenAdmin}>
                  <ListItemIcon>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={getLocalizedMessage(
                      language,
                      "userMenu.adminPanel"
                    )}
                  />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem>
              <ChangeLanguageButton
                language={language}
                onLanguageChange={(newLanguage) =>
                  fireEvent?.("change-language", { language: newLanguage })
                }
                isMenuCmponent
              />
            </ListItem>
            <ListItem>
              <ListItemButton
                onClick={() => {
                  window.open("mailto:contact@pimoapp.com");
                }}
              >
                <ListItemIcon>
                  <Email />
                </ListItemIcon>
                <ListItemText
                  primary={getLocalizedMessage(
                    language,
                    "userMenu.contactPimoTeam"
                  )}
                />
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary={getLocalizedMessage(language, "userMenu.logout")}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Popover>
    </>
  );
};
